import BaseModal from "@components/BaseModal";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Button} from "@/components/Button";
import {getStorage, setStorage} from "@/store/util.store";
import useGetBannerList from "../../../../../hooks/useGetBanners";
import {useAuthStore} from "../../../../../store/useAuth.store";
import styles from "./AppBanners.module.css";

const SAME_BANNER_INTERVAL = 86400000; // 24hr 86400000 - test 60000 1m
const FIRST_BANNER_SHOW = 6000; // First banner show in 6s
const NEW_BANNER_CHECK = 3600000; // Check every hour 3600000 if no banner - test 60000 1m
const NEXT_BANNER_SHOW = 300000; // Next banner show in 5min - test 6000 6s

const AppBanners = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const authStatus = useAuthStore(state => state.authStatus);
  const {data: banners, isLoading} = useGetBannerList();
  const timerRef = useRef(null);

  const {t} = useTranslation();

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const canShowBanner = useCallback(bannerId => {
    const lastShown = getStorage(`banner_${bannerId}_lastShown`);
    if (!lastShown) {
      return true;
    }
    return Date.now() - Number(lastShown) > SAME_BANNER_INTERVAL;
  }, []);

  const findAvailableBanner = useCallback(() => {
    if (!banners || banners.length === 0) {
      return null;
    }
    return banners.find(banner => canShowBanner(banner.id));
  }, [banners, canShowBanner]);

  const showBanner = useCallback(banner => {
    if (!banner) {
      return;
    }

    const img = new Image();
    img.src = `https://images.cdn.randrush.com/images/${banner.image_key}`;

    setCurrentBanner(banner);
    setIsModalOpen(true);
    setStorage(`banner_${banner.id}_lastShown`, Date.now().toString());
  }, []);

  const checkAndShowBanner = useCallback(() => {
    const banner = findAvailableBanner();
    if (banner) {
      showBanner(banner);
    } else {
      timerRef.current = setTimeout(checkAndShowBanner, NEW_BANNER_CHECK);
    }
  }, [findAvailableBanner, showBanner]);

  // Initial check for banners when authStatus changes to authenticated and banners are loaded
  useEffect(() => {
    if (authStatus === "authenticated" && banners) {
      // Initial check with a slight delay to ensure everything is rendered
      timerRef.current = setTimeout(checkAndShowBanner, FIRST_BANNER_SHOW);
    }

    return () => clearTimeout(timerRef.current);
  }, [authStatus, banners, checkAndShowBanner]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    // Reschedule the banner check after closing
    timerRef.current = setTimeout(checkAndShowBanner, NEXT_BANNER_SHOW);
  }, [checkAndShowBanner]);

  return (
    <div>
      {authStatus === "authenticated" && currentBanner && (
        <BaseModal
          title=""
          open={isModalOpen}
          onCancel={handleCancel}
          footer={null}
          closable={false}
          destroyOnHidden
          zIndex={9000}
          className="app-banner-modal"
          centered
          styles={{
            content: {padding: 0, overflow: "hidden", borderRadius: 20},
            body: {padding: 0},
          }}
        >
          {isLoading ? (
            <p>Loading banner...</p>
          ) : (
            <div className={styles.wrapper}>
              <img
                src={`https://images.cdn.randrush.com/images/${currentBanner.image_key}`}
                alt=""
                className={styles.banner}
              />
              <div className={styles.footer}>
                <Button
                  variant="tertiary-hover"
                  onClick={handleCancel}
                  className={styles.closeBtn}
                >
                  {t("homeDisplayModalClose")}
                </Button>
              </div>
            </div>
          )}
        </BaseModal>
      )}
    </div>
  );
};

export default AppBanners;
